import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { OnboardingProvider, useOnboardingState, useOnboardingDispatch, clearOnboardingDraft } from '../../context/OnboardingContext';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import StepSidebar from './StepSidebar';
import { useLanguage } from '../../i18n/LanguageContext';
import { useStepNavigation } from '../../hooks/useStepNavigation';

const stepRoutes: Record<number, string> = {
  1: '/onboarding/license-and-id',
  2: '/onboarding/garage-info',
  3: '/onboarding/terms',
};

/** Map old backend step values to the new 3-step model */
function mapBackendStep(backendStep: number): number {
  if (backendStep <= 2) return backendStep; // Steps 1-2 unchanged
  return 3; // Steps 3+ all map to step 3 (terms)
}

const ONBOARDING_USER_KEY = 'garagat_onboarding_user';

function clearStaleSession(currentUserId: string | undefined) {
  if (!currentUserId) return;
  const savedUserId = localStorage.getItem(ONBOARDING_USER_KEY);
  if (savedUserId !== currentUserId) {
    clearOnboardingDraft();
  }
  localStorage.setItem(ONBOARDING_USER_KEY, currentUserId);
}

function AppSidebar({ user, onLogout, onShowProfile, showProfile }: {
  user: { name: string; email?: string | null } | null;
  onLogout: () => void;
  onShowProfile: () => void;
  showProfile: boolean;
}) {
  const { t } = useLanguage();
  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const handleLogout = async () => {
    await onLogout();
    window.location.href = '/login';
  };

  return (
    <aside className="app-sidebar">
      <div className="app-sidebar-logo">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="15" stroke="#e5c526" strokeWidth="2" />
          <text x="16" y="21" textAnchor="middle" fill="#e5c526" fontSize="14" fontWeight="700">G</text>
        </svg>
      </div>

      <div className="app-sidebar-divider" />

      <div className="app-sidebar-user" onClick={onShowProfile} style={{ cursor: 'pointer' }} title={t('onboarding.tooltip.viewProfile')}>
        <div className={`app-sidebar-avatar${showProfile ? ' active' : ''}`}>{initials}</div>
      </div>

      <nav className="app-sidebar-nav">
        <button
          className={`app-sidebar-btn${!showProfile ? ' active' : ''}`}
          title={t('onboarding.tooltip.onboarding')}
          onClick={() => { if (showProfile) window.location.href = '/onboarding/license-and-id'; }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
        </button>
      </nav>

      <div className="app-sidebar-spacer" />

      <button
        className="app-sidebar-btn"
        title={t('onboarding.tooltip.saveLater')}
        onClick={handleLogout}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
      </button>

      <div className="app-sidebar-divider" />

      <button
        className="app-sidebar-btn app-sidebar-logout"
        title={t('onboarding.tooltip.logout')}
        onClick={handleLogout}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
      </button>
    </aside>
  );
}

function UserProfilePanel({ user }: { user: { name: string; email?: string | null } | null }) {
  const { t } = useLanguage();
  const nameParts = (user?.name || '').split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return (
    <div>
      <h2 className="wizard-step-title">{t('onboarding.profile.title')}</h2>
      <p style={{ color: '#6b7280', fontSize: '0.9375rem', marginBottom: '1.5rem' }}>
        {t('onboarding.profile.subtitle')}
      </p>
      <div className="wizard-form-row">
        <div className="wizard-form-group">
          <label>{t('auth.firstName')}</label>
          <input type="text" value={firstName} disabled />
        </div>
        <div className="wizard-form-group">
          <label>{t('auth.lastName')}</label>
          <input type="text" value={lastName} disabled />
        </div>
      </div>
      <div className="wizard-form-group">
        <label>{t('auth.email')}</label>
        <input type="email" value={user?.email || ''} disabled />
      </div>
    </div>
  );
}

function OnboardingContent() {
  const state = useOnboardingState();
  const dispatch = useOnboardingDispatch();
  // Sidebar highlight follows the URL (single source of truth), not the context step,
  // which can drift from the route after a refresh or back-navigation (#430).
  const { currentStep } = useStepNavigation();
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [initError, setInitError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showProfile, setShowProfile] = useState(false);
  // Single-run guard. Survives re-renders; cleared on retry / token change.
  // Putting state.garageId / draftLoaded in the deps array (the old pattern)
  // re-fired the effect mid-flight and produced a duplicate /api/garages/init
  // call within ~50ms of the first.
  const initStartedRef = useRef(false);

  useEffect(() => {
    if (!auth.token) return;
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    const controller = new AbortController();

    (async () => {
      try {
        const garage = await api.initGarage(auth.token!, controller.signal);
        if (controller.signal.aborted) return;

        dispatch({ type: 'SET_GARAGE_ID', garageId: garage.id });
        setInitError(null);

        if (garage.status === 'DRAFT') {
          try {
            const detail = await api.getMyGarage(auth.token!);
            if (controller.signal.aborted) return;
            dispatch({ type: 'LOAD_FROM_BACKEND', detail });

            const savedStep = mapBackendStep(detail.onboardingStep || 1);
            const route = stepRoutes[savedStep];
            if (route) {
              navigate(route, { replace: true });
            }
          } catch {
            // Non-critical
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        // Allow another attempt after a real failure.
        initStartedRef.current = false;
        setInitError(err instanceof Error ? err.message : t('onboarding.initFailed'));
      }
    })();

    return () => controller.abort();
    // Re-run only on token change or explicit user retry. dispatch + navigate
    // are stable references; pulling them in or referencing state set by this
    // effect would re-fire it mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token, retryCount]);

  // When user clicks Retry, allow a fresh attempt.
  useEffect(() => {
    initStartedRef.current = false;
  }, [retryCount]);

  const completedSteps: number[] = [];
  if (
    state.tradeLicense.licenseNumber &&
    state.tradeLicense.files.length > 0 &&
    state.ownerEmiratesId.files.length > 0
  ) completedSteps.push(1);
  if (state.garageInfo.name && state.garageInfo.phone && state.garageInfo.city) completedSteps.push(2);

  if (initError) {
    return (
      <div className="onboarding-page">
        <AppSidebar user={auth.user} onLogout={auth.logout} onShowProfile={() => setShowProfile(true)} showProfile={showProfile} />
        <div className="onboarding-body">
          <div className="onboarding-header">
            <h1>{t('onboarding.setupError')}</h1>
            <p className="error-message">{initError}</p>
            <button className="wizard-btn wizard-btn-primary" onClick={() => { setInitError(null); setRetryCount(c => c + 1); }}>
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-page">
      <AppSidebar user={auth.user} onLogout={auth.logout} onShowProfile={() => setShowProfile(p => !p)} showProfile={showProfile} />
      <div className="onboarding-body">
        <div className="onboarding-header">
          <div className="onboarding-welcome">{t('onboarding.welcome')}</div>
          {showProfile ? (
            <>
              <h1>{t('onboarding.profile.title')}</h1>
              <p>{t('onboarding.profile.subtitle')}</p>
            </>
          ) : (
            <>
              <h1><strong>{t('onboarding.title.bold')}</strong> {t('onboarding.title.rest')}</h1>
              <p>{t('onboarding.subtitle')}</p>
            </>
          )}
        </div>
        <div className="onboarding-content">
          {!showProfile && (
            <div className="onboarding-stepper-panel">
              <StepSidebar currentStep={currentStep} completedSteps={completedSteps} />
            </div>
          )}
          <div className="onboarding-main-panel">
            {showProfile ? <UserProfilePanel user={auth.user} /> : <Outlet />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OnboardingLayout() {
  const auth = useAuth();

  clearStaleSession(auth.user?.id);

  return (
    <OnboardingProvider>
      <OnboardingContent />
    </OnboardingProvider>
  );
}
